let registeredSocketCloser = null;
let registeredSocketCloserOwner = null;
let authSessionGeneration = 0;
let terminatedSessionGeneration = null;
let terminationPromise = null;

const DEFAULT_SOCKET_CLOSE_TIMEOUT_MS = 1000;

const createTerminationResult = ({
  terminationPerformed,
  alreadyTerminated = false,
  navigationAttempted = false,
  sessionGeneration,
}) => ({
  terminationPerformed,
  alreadyTerminated,
  navigationAttempted,
  sessionGeneration,
});

const runWithTimeout = (operation, timeoutMs) => new Promise((resolve) => {
  let settled = false;
  const timer = setTimeout(() => {
    if (settled) return;
    settled = true;
    resolve();
  }, timeoutMs);

  let result;
  try {
    result = operation();
  } catch (e) {
    result = Promise.reject(e);
  }

  Promise.resolve(result)
    .catch(() => {})
    .finally(() => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve();
    });
});

const safelyRun = async (operation) => {
  if (typeof operation !== 'function') return false;

  try {
    await operation();
  } catch (e) {
    // Auth termination must complete without exposing local auth/socket details.
  }
  return true;
};

export const registerAuthSocketCloser = (socketCloser) => {
  const owner = Symbol('auth-socket-closer-owner');
  if (
    typeof socketCloser === 'function' &&
    !terminationPromise &&
    (authSessionGeneration === 0 || terminatedSessionGeneration === authSessionGeneration)
  ) {
    authSessionGeneration += 1;
    terminatedSessionGeneration = null;
  }

  registeredSocketCloser = typeof socketCloser === 'function' ? socketCloser : null;
  registeredSocketCloserOwner = owner;

  return () => {
    if (registeredSocketCloserOwner !== owner) return;
    registeredSocketCloser = null;
    registeredSocketCloserOwner = null;
  };
};

export const resetAuthSessionTerminationForTests = () => {
  registeredSocketCloser = null;
  registeredSocketCloserOwner = null;
  authSessionGeneration = 0;
  terminatedSessionGeneration = null;
  terminationPromise = null;
};

export const terminateAuthSession = ({
  socketCloser,
  clearAuthStorage = () => localStorage.clear(),
  dispatchLogoutCleanup,
  showLogoutToast,
  navigateToLogin = () => {
    window.location.href = '/login';
  },
  socketCloseTimeoutMs = DEFAULT_SOCKET_CLOSE_TIMEOUT_MS,
} = {}) => {
  if (terminationPromise) return terminationPromise;
  if (terminatedSessionGeneration === authSessionGeneration) {
    return Promise.resolve(createTerminationResult({
      terminationPerformed: false,
      alreadyTerminated: true,
      navigationAttempted: false,
      sessionGeneration: authSessionGeneration,
    }));
  }

  const sessionGeneration = authSessionGeneration;
  terminationPromise = (async () => {
    const closeSocket = socketCloser || registeredSocketCloser;

    if (typeof closeSocket === 'function') {
      await runWithTimeout(closeSocket, socketCloseTimeoutMs);
    }

    await safelyRun(clearAuthStorage);
    await safelyRun(dispatchLogoutCleanup);
    await safelyRun(showLogoutToast);
    const navigationAttempted = await safelyRun(navigateToLogin);

    return createTerminationResult({
      terminationPerformed: true,
      alreadyTerminated: false,
      navigationAttempted,
      sessionGeneration,
    });
  })().finally(() => {
    terminatedSessionGeneration = sessionGeneration;
    terminationPromise = null;
  });

  return terminationPromise;
};

export const scheduleAuthSessionTermination = ({
  timeoutRef,
  delayMs,
  terminate,
}) => {
  if (!timeoutRef || timeoutRef.current) return false;

  timeoutRef.current = setTimeout(() => {
    timeoutRef.current = null;
    terminateAuthSession(terminate);
  }, delayMs);

  return true;
};
